import { c } from "react/compiler-runtime";
import { useCache } from "@effektify/cache";
import useLogger from "./useLogger.js";
const supportedOperators = ["eq", "neq"];
const isVariable = variable => typeof variable === "object" && variable !== null && "variable" in variable && typeof variable.variable === "string";
const isConditionalOperation = operation => Array.isArray(operation) && operation.every(item => isValidOperation(item));
const isValidOperation = operation => !!operation && typeof operation === "object" && "key" in operation && isVariable(operation.key) && "op" in operation && typeof operation.op === "string" && (operation.op === "eq" || operation.op === "neq") && "value" in operation && (typeof operation.value === "boolean" || typeof operation.value === "string");
const isAndOperation = operation => typeof operation === "object" && operation !== null && "and" in operation && Array.isArray(operation.and);
const isOrOperation = operation => typeof operation === "object" && operation !== null && "or" in operation && Array.isArray(operation.or);
const hasMetCondition = (condition, variableFlags) => {
  if (isValidOperation(condition)) {
    const variableName = condition.key.variable;
    const variableValue = variableFlags[variableName];
    if (variableValue === void 0 || !supportedOperators.includes(condition.op)) {
      return false;
    }
    return condition.op === "eq" ? variableValue === condition.value : variableValue !== condition.value;
  }
  return false;
};
const isValidVariables = variables => typeof variables === "object" && variables !== null && Object.keys(variables).every(key => typeof variables[key] === "boolean" || typeof variables[key] === "string" || variables[key] === null);
const resolveConditions = (conditions, variableFlags, logger) => {
  if (isAndOperation(conditions)) {
    const result = conditions.and.every(condition => hasMetCondition(condition, variableFlags));
    logger.debug(`Operation ${conditions.and.map(c2 => `${c2.key.variable} ${c2.op === "eq" ? "===" : "!=="} ${c2.value}`).join(" && ")} => ${result} `);
    return result;
  }
  if (isOrOperation(conditions)) {
    const result = conditions.or.some(condition => hasMetCondition(condition, variableFlags));
    logger.debug(`Operation ${conditions.or.map(c2 => `${c2.key.variable} ${c2.op === "eq" ? "===" : "!=="} ${c2.value}`).join(" || ")} => ${result} `);
    return result;
  }
  if (isConditionalOperation(conditions)) {
    const result = conditions.every(condition => hasMetCondition(condition, variableFlags));
    logger.debug(`Operation ${conditions.map(c2 => `${c2.key.variable} ${c2.op === "eq" ? "===" : "!=="} ${c2.value}`).join("; ")} => ${result} `);
    return result;
  }
  console.warn(`Invalid condition format: ${JSON.stringify(conditions)}`);
  return false;
};
const useVariables = () => {
  const $ = c(7);
  const {
    variables
  } = useCache();
  const logger = useLogger("useVariables");
  const data = variables.data;
  let t0;
  if ($[0] !== data) {
    t0 = data && Object.keys(data).length > 0 ? data : null;
    $[0] = data;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const variableFlags = t0;
  let t1;
  if ($[2] !== logger || $[3] !== variableFlags) {
    t1 = conditions => {
      if (!variableFlags || !isValidVariables(variableFlags)) {
        return false;
      }
      return resolveConditions(conditions, variableFlags, logger);
    };
    $[2] = logger;
    $[3] = variableFlags;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const hasMetConditions = t1;
  let t2;
  if ($[5] !== hasMetConditions) {
    t2 = {
      hasMetConditions
    };
    $[5] = hasMetConditions;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
export { useVariables as default };